import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { readError } from '../../lib/errors'

/**
 * Admin dashboard for Claude (AI) usage.
 *
 * Mounted at `/admin/usage` via main.tsx. Three queries:
 *   - admin_claude_usage_by_game       (lifetime totals + averages)
 *   - admin_claude_usage_daily(30)     (last 30 days trend)
 *   - admin_claude_usage_top_users(25) (heaviest spenders)
 *
 * The RPCs check `is_admin()` server-side, so this page is just a
 * thin renderer — anyone signed in could navigate to /admin/usage,
 * but the queries return "Admin only" if they're not on the email
 * allowlist (`is_admin()` in SQL).
 */

interface ByGameRow {
  game_id: string
  call_count: number
  session_count: number
  user_count: number
  total_cost_usd: number
  avg_cost_per_call: number
  error_count: number
}

interface DailyRow {
  day: string
  game_id: string
  call_count: number
  cost_usd: number
}

interface TopUserRow {
  caller_user_id: string
  display_name: string | null
  email: string | null
  call_count: number
  total_cost_usd: number
  game_count: number
  games_played: number
  first_call_at: string
  last_call_at: string
}

const GAME_LABEL: Record<string, string> = {
  ideatthat:  "I'D EAT THAT",
  colorful:   'Colorful',
  three_step: 'THREE STEP SOLUTION',
  tokyo_metro:'TOKYO METRO',
}

function gameLabel(id: string): string {
  return GAME_LABEL[id] ?? id
}

function fmtUsd(v: number | null | undefined): string {
  const n = Number(v ?? 0)
  if (n < 0.01) return `$${n.toFixed(4)}`
  if (n < 1)    return `$${n.toFixed(3)}`
  return `$${n.toFixed(2)}`
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

export function AdminUsage() {
  const { session, profile } = useAuth()
  const [byGame, setByGame] = useState<ByGameRow[] | null>(null)
  const [daily, setDaily]   = useState<DailyRow[] | null>(null)
  const [users, setUsers]   = useState<TopUserRow[] | null>(null)
  const [error, setError]   = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!session) { setLoading(false); return }
    let cancelled = false
    async function load() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sb = supabase as any
        const [g, d, u] = await Promise.all([
          sb.rpc('admin_claude_usage_by_game'),
          sb.rpc('admin_claude_usage_daily', { p_days: 30 }),
          sb.rpc('admin_claude_usage_top_users', { p_limit: 25 }),
        ])
        if (cancelled) return
        if (g.error) throw g.error
        if (d.error) throw d.error
        if (u.error) throw u.error
        setByGame(g.data ?? [])
        setDaily(d.data ?? [])
        setUsers(u.data ?? [])
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [session?.user?.id])

  // Daily aggregated across all games for the trend total
  const dailyTotals = (daily ?? []).reduce<Record<string, number>>((acc, r) => {
    acc[r.day] = (acc[r.day] ?? 0) + Number(r.cost_usd)
    return acc
  }, {})
  const trendDays = Object.keys(dailyTotals).sort()
  const maxCost = Math.max(0.0001, ...Object.values(dailyTotals))
  const grandTotal = (byGame ?? []).reduce((s, r) => s + Number(r.total_cost_usd), 0)
  const grandCalls = (byGame ?? []).reduce((s, r) => s + Number(r.call_count), 0)

  if (!session) {
    return (
      <Page>
        <h2 className="font-display text-2xl font-bold text-brand-text">Sign in required</h2>
        <p className="text-sm text-brand-muted mt-2">
          Open the app, sign in with your admin account, then return to this URL.
        </p>
      </Page>
    )
  }

  if (loading) {
    return (
      <Page>
        <div className="space-y-4">
          <div className="h-32 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-64 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-64 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </Page>
    )
  }

  if (error) {
    return (
      <Page>
        <div className="card p-5 bg-brand-danger/10 border-brand-danger/30 border">
          <p className="font-semibold text-brand-danger">Error loading usage</p>
          <p className="text-sm text-brand-text mt-1">{error}</p>
          <p className="text-xs text-brand-muted mt-3">
            If this says "Admin only", you're signed in as a non-admin account.
            Add your email to <span className="font-mono">is_admin()</span> in
            SQL and reload.
          </p>
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black text-brand-text">AI Usage</h1>
        <p className="text-sm text-brand-muted mt-1">
          Signed in as {profile?.display_name ?? 'admin'} ·{' '}
          {grandCalls.toLocaleString()} total Claude calls ·{' '}
          {fmtUsd(grandTotal)} lifetime spend
        </p>
      </div>

      {/* By-game lifetime summary */}
      <section className="mb-8">
        <h2 className="font-semibold text-brand-text mb-3">Lifetime by game</h2>
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-elevated text-xs uppercase tracking-wide text-brand-muted">
              <tr>
                <th className="text-left px-4 py-2">Game</th>
                <th className="text-right px-4 py-2">Calls</th>
                <th className="text-right px-4 py-2">Sessions</th>
                <th className="text-right px-4 py-2">Users</th>
                <th className="text-right px-4 py-2">Avg/call</th>
                <th className="text-right px-4 py-2">Errors</th>
                <th className="text-right px-4 py-2">Total $</th>
              </tr>
            </thead>
            <tbody>
              {(byGame ?? []).map(row => (
                <tr key={row.game_id} className="border-t border-brand-border">
                  <td className="px-4 py-3 font-semibold text-brand-text">
                    {gameLabel(row.game_id)}
                  </td>
                  <td className="px-4 py-3 text-right">{row.call_count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">{row.session_count}</td>
                  <td className="px-4 py-3 text-right">{row.user_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {fmtUsd(row.avg_cost_per_call)}
                  </td>
                  <td className={`px-4 py-3 text-right ${row.error_count > 0 ? 'text-brand-danger' : 'text-brand-muted'}`}>
                    {row.error_count}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    {fmtUsd(row.total_cost_usd)}
                  </td>
                </tr>
              ))}
              {(byGame ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-brand-muted">
                    No AI usage logged yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Daily trend (last 30 days) */}
      <section className="mb-8">
        <h2 className="font-semibold text-brand-text mb-3">Last 30 days</h2>
        <div className="card p-4">
          {trendDays.length === 0 ? (
            <p className="text-sm text-brand-muted text-center py-8">
              No calls in the last 30 days.
            </p>
          ) : (
            <div className="space-y-1">
              {trendDays.map(day => {
                const cost = dailyTotals[day]
                const pct = (cost / maxCost) * 100
                return (
                  <div key={day} className="flex items-center gap-3 text-xs">
                    <span className="w-20 text-brand-muted shrink-0">{fmtDate(day)}</span>
                    <div className="flex-1 h-5 bg-brand-elevated rounded-xs overflow-hidden relative">
                      <div
                        className="h-full bg-brand-lime"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-20 text-right tabular-nums font-semibold">
                      {fmtUsd(cost)}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Top spenders */}
      <section>
        <h2 className="font-semibold text-brand-text mb-3">Top users</h2>
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-elevated text-xs uppercase tracking-wide text-brand-muted">
              <tr>
                <th className="text-left px-4 py-2">Player</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-right px-4 py-2">Calls</th>
                <th className="text-right px-4 py-2">Games</th>
                <th className="text-right px-4 py-2">Last seen</th>
                <th className="text-right px-4 py-2">Total $</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map(row => (
                <tr key={row.caller_user_id} className="border-t border-brand-border">
                  <td className="px-4 py-3 font-semibold text-brand-text">
                    {row.display_name ?? '…'}
                  </td>
                  <td className="px-4 py-3 text-brand-muted text-xs">
                    {row.email ?? ''}
                  </td>
                  <td className="px-4 py-3 text-right">{row.call_count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">{row.game_count}</td>
                  <td className="px-4 py-3 text-right text-brand-muted text-xs">
                    {fmtDate(row.last_call_at)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    {fmtUsd(row.total_cost_usd)}
                  </td>
                </tr>
              ))}
              {(users ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-brand-muted">
                    No user-attributed AI usage yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </Page>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-brand-bg pb-20">
      <div className="mx-auto max-w-4xl px-5 py-8">
        {children}
      </div>
    </div>
  )
}
